import { Box, Typography, Paper, List, ListItem, ListItemText, Chip, Button, CircularProgress, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const Logs = () => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const logsPerPage = 100;
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({
    start: null,
    end: null
  });

  const fetchLogs = async () => {
    try {
      setIsLoading(true);
      let query = supabase
        .from('logs')
        .select('*')
        .order('created_at', { ascending: sortOrder === 'asc' });

      if (filterType !== 'all') {
        query = query.eq('type', filterType);
      }

      if (searchTerm) {
        query = query.ilike('message', `%${searchTerm}%`);
      }

      if (dateRange.start && dateRange.end) {
        query = query
          .gte('created_at', dateRange.start.toISOString())
          .lte('created_at', dateRange.end.toISOString());
      }

      const { data, error } = await query
        .range(page * logsPerPage, (page + 1) * logsPerPage - 1);

      if (error) throw error;
      
      if (page === 0) {
        setLogs(data);
      } else {
        setLogs(prev => [...prev, ...data]);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page]);

  const getChipColor = (type) => {
    switch (type) {
      case 'Delivery':
        return '#90EE90';
      case 'Payment':
        return '#FFB6C1';
      case 'Info':
        return '#FFFF00';
      default:
        return '#e0e0e0';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>Activity Logs</Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <TextField
            label="Search Logs"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1 }}
          />
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              label="Type"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="Delivery">Delivery</MenuItem>
              <MenuItem value="Payment">Payment</MenuItem>
              <MenuItem value="Info">Info</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Sort</InputLabel>
            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              label="Sort"
            >
              <MenuItem value="desc">Newest First</MenuItem>
              <MenuItem value="asc">Oldest First</MenuItem>
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={dateRange.start}
              onChange={(newValue) => setDateRange(prev => ({ ...prev, start: newValue }))}
              slots={{
                textField: TextField
              }}
            />
            <DatePicker
              label="End Date"
              value={dateRange.end}
              onChange={(newValue) => setDateRange(prev => ({ ...prev, end: newValue }))}
              slots={{
                textField: TextField
              }}
            />
          </LocalizationProvider>
          <Button
            onClick={() => {
              setPage(0);
              fetchLogs();
            }}
            variant="contained"
            sx={{ 
              backgroundColor: '#e9c0e9',
              color: 'black',
              '&:hover': { backgroundColor: '#d4abd4' }
            }}
          >
            Apply Filters
          </Button>
        </Box>
        
        <List sx={{ width: '100%' }}>
          {logs.map((log) => (
            <Box 
              key={log.id} 
              sx={{ 
                display: 'flex',
                alignItems: 'flex-start',  // Align items to top
                gap: 2,
                mb: 2  // Margin bottom for spacing between items
              }}
            >
              <Chip 
                label={log.type} 
                sx={{ 
                  backgroundColor: getChipColor(log.type),
                  minWidth: '80px',  // Fixed width for chips
                  mt: 0.5  // Small top margin to align with text
                }} 
              />
              <Box>
                <Typography>{log.message}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(log.created_at).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          ))}
          {logs.length === 0 && (
            <ListItem>
              <ListItemText
                primary={
                  <Typography color="text.secondary">
                    No activity logs yet
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </Paper>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
        <Button
          onClick={() => setPage(prev => prev + 1)}
          disabled={isLoading}
          variant="contained"
          sx={{ 
            backgroundColor: '#e9c0e9',
            color: 'black',
            '&:hover': { backgroundColor: '#d4abd4' }
          }}
        >
          {isLoading ? (
            <CircularProgress size={24} />
          ) : (
            'Show Next 100'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default Logs; 